import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '@ant-design/icons';

import { ReactComponent as SafetyAnomalyMonitoringSvg } from '../../assets/icons/alert-list.svg';
import { ReactComponent as SafetyAnomalyMonitoringBlackSvg } from '../../assets/icons/alert-list-black.svg';
import { ReactComponent as OverviewSvg } from '../../assets/icons/desktop-dashboard.svg';
import { ReactComponent as OverviewBlackSvg } from '../../assets/icons/desktop-dashboard-black.svg';
import { ReactComponent as VehicleMonitoringSvg } from '../../assets/icons/car-chip.svg';
import { ReactComponent as VehicleMonitoringBlackSvg } from '../../assets/icons/car-chip-black.svg';
import { ReactComponent as BroadcastSvg } from '../../assets/icons/broadcast.svg';
import { ReactComponent as BroadcastBlackSvg } from '../../assets/icons/broadcast-black.svg';
import { ReactComponent as DeviceManagementSvg } from '../../assets/icons/device-management.svg';
import { ReactComponent as DeviceManagementBlackSvg } from '../../assets/icons/device-management-black.svg';
import { ReactComponent as HistoryAndReportsSvg } from '../../assets/icons/history-and-reports.svg';
import { ReactComponent as HistoryAndReportsBlackSvg } from '../../assets/icons/history-and-reports-black.svg';
import { ReactComponent as DMSSvg } from '../../assets/icons/dms.svg';
import { ReactComponent as DMSBlackSvg } from '../../assets/icons/dms-black.svg';
import { ReactComponent as UserManagementSvg } from '../../assets/icons/user-management.svg';
import { ReactComponent as UserManagementBlackSvg } from '../../assets/icons/user-management-black.svg';
import { ReactComponent as TripSurveillanceAndAnalysisSvg } from '../../assets/icons/trip-surveillance-and-analysis.svg';
import { ReactComponent as TripSurveillanceAndAnalysisBlackSvg } from '../../assets/icons/trip-surveillance-and-analysis-black.svg';
import { ReactComponent as TripSchedulingSvg } from '../../assets/icons/trip-scheduling.svg';
import { ReactComponent as TripSchedulingBlackSvg } from '../../assets/icons/trip-scheduling-black.svg';

import { useUserRolesAndPermissions } from '../../context/UserRolesAndPermissionsContext';
import { PAGE_KEYS, USER_ROLE_KEYS } from '../../utilities/constants';
import { useTheme } from '../../context/ThemeContext';

const NAV_ITEMS_CONFIG = [
  {
    key: '/overview',
    label: 'Overview',
    icon: OverviewSvg,
    iconBlack: OverviewBlackSvg,
    pageKey: PAGE_KEYS.OVERVIEW,
    path: '/overview',
  },
  {
    key: '/vehicle-monitoring',
    label: 'Vehicle Monitoring',
    icon: VehicleMonitoringSvg,
    iconBlack: VehicleMonitoringBlackSvg,
    pageKey: PAGE_KEYS.VEHICLE_MONITORING,
    path: '/vehicle-monitoring',
  },
  {
    key: '/driver-monitoring-system',
    label: 'Driver Monitoring System (DMS)',
    icon: DMSSvg,
    iconBlack: DMSBlackSvg,
    pageKey: PAGE_KEYS.DRIVER_MONITORING_SYSTEM,
    path: '/driver-monitoring-system',
    featureKey: 'DMS',
  },
  {
    key: '/trip-surveillance-and-analysis',
    label: 'Trip Surveillance and Analysis',
    icon: TripSurveillanceAndAnalysisSvg,
    iconBlack: TripSurveillanceAndAnalysisBlackSvg,
    pageKey: PAGE_KEYS.TRIP_SURVEILLANCE_AND_ANALYSIS,
    path: '/trip-surveillance-and-analysis',
    featureKey: 'VoD',
  },
  {
    key: '/broadcast',
    label: 'Broadcast',
    icon: BroadcastSvg,
    iconBlack: BroadcastBlackSvg,
    pageKey: PAGE_KEYS.BROADCAST,
    path: '/broadcast',
  },
  {
    key: '/safety-and-anomaly-monitoring',
    label: 'Safety & Anomaly Monitoring',
    icon: SafetyAnomalyMonitoringSvg,
    iconBlack: SafetyAnomalyMonitoringBlackSvg,
    pageKey: PAGE_KEYS.SAFETY_AND_ANOMALY_MONITORING,
    path: '/safety-and-anomaly-monitoring',
  },
  {
    key: '/history-and-reports',
    label: 'History and Reports',
    icon: HistoryAndReportsSvg,
    iconBlack: HistoryAndReportsBlackSvg,
    pageKey: PAGE_KEYS.HISTORY_AND_REPORTS,
    path: '/history-and-reports/trips-report',
  },
  {
    key: '/device-management',
    label: 'Device Management',
    icon: DeviceManagementSvg,
    iconBlack: DeviceManagementBlackSvg,
    pageKey: PAGE_KEYS.DEVICE_MANAGEMENT,
    path: '/device-management',
    roleExclusion: [
      USER_ROLE_KEYS.PLATFORM_OPERATION_MANAGER,
      USER_ROLE_KEYS.SUPER_ADMIN,
    ],
  },
  {
    key: '/user-management',
    label: 'User Management',
    icon: UserManagementSvg,
    iconBlack: UserManagementBlackSvg,
    pageKey: PAGE_KEYS.USER_MANAGEMENT,
    path: '/user-management',
  },
  {
    key: '/trip-scheduling-and-management',
    label: 'Trip Scheduling and Management',
    icon: TripSchedulingSvg,
    iconBlack: TripSchedulingBlackSvg,
    pageKey: PAGE_KEYS.TRIP_SCHEDULING_AND_MANAGEMENT,
    path: '/trip-scheduling-and-management',
  },
];

export const useNavigationItemsList = setCollapsed => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, isPageAccessible, isFeatureAccessible } =
    useUserRolesAndPermissions();

  const [navigationListItems, setNavigationListItems] = useState([]);
  const { theme } = useTheme();

  useEffect(() => {
    const items = NAV_ITEMS_CONFIG.filter(item => {
      if (!isPageAccessible(item.pageKey)) return false;

      if (item.featureKey && !isFeatureAccessible(item.featureKey))
        return false;

      if (item?.roleExclusion?.includes(userRole)) return false;

      return true;
    }).map(item => {
      const isActive = location.pathname.startsWith(item.key);
      const iconComponent =
        theme !== 'dark' && isActive ? item.iconBlack || item.icon : item.icon;

      return {
        key: item.key,
        icon: <Icon component={iconComponent} />,
        label: item.label,
        onClick: () => {
          navigate(item.path);
          if (setCollapsed) setCollapsed(true);
        },
      };
    });

    setNavigationListItems(items);
  }, [
    userRole,
    isPageAccessible,
    isFeatureAccessible,
    navigate,
    location.pathname,
    setCollapsed,
    theme,
  ]);

  return { navigationListItems };
};
